import { z } from 'zod';

/**
 * Conversation-summary artifact contracts — the stored shapes for
 * `conversation_summaries` and the strict wire contract the summarizer model
 * must satisfy. Stored shapes are camelCase (Galapagos-owned jsonb, ADR-066);
 * the model wire is snake_case to match how the prompt names the fields.
 *
 * Governed enum sources (ADR-056 Rule 8): the `as const` arrays back both the
 * Zod schemas and the text+CHECK constraints in db/schema.ts, so the DB and
 * the contract cannot drift.
 */

/** Terminal row statuses. Skip statuses persist artifact-less rows so the reconciliation sweep never re-drives a conversation that was deliberately not summarized. */
export const SUMMARY_STATUS = [
  'generated',
  'degraded',
  'skipped_unattributed',
  'skipped_empty',
] as const;
export const summaryStatusSchema = z.enum(SUMMARY_STATUS);
export type SummaryStatus = z.infer<typeof summaryStatusSchema>;

/** Agent-adapted clinician section vocabulary — deliberately not SOAP: an agent conversation has no exam or clinician assessment. Clinical review may extend this additively before the display slice enables. */
export const CLINICIAN_SECTION_KEY = [
  'presenting_focus',
  'member_report',
  'commitments_next_steps',
  'risk_signals',
  'practices_used',
] as const;
export const clinicianSectionKeySchema = z.enum(CLINICIAN_SECTION_KEY);
export type ClinicianSectionKey = z.infer<typeof clinicianSectionKeySchema>;

/** Closed, condition-agnostic topic vocabulary (ADR-015: no substance-specific values). Analytics correlation requires a closed set — free-text topics never aggregate. */
export const SUMMARY_TOPIC = [
  'substance_or_behavior_use',
  'cravings_urges',
  'mood_feelings',
  'sleep',
  'relationships_family',
  'work_life_stress',
  'goal_setting',
  'progress_reflection',
  'coping_strategies',
  'physical_health',
  'motivation_ambivalence',
  'crisis_distress',
  'practice_exercise',
  'plan_adjustment',
  'logistics_smalltalk',
] as const;
export const summaryTopicSchema = z.enum(SUMMARY_TOPIC);
export type SummaryTopic = z.infer<typeof summaryTopicSchema>;

/** Topics stored per conversation are de-duplicated and capped, keeping the model's order. */
export const MAX_SUMMARY_TOPICS = 5;

/** Member-copy ceilings the safety gate enforces by shortening in place (never by throwing — a temperature-0 retry reproduces the same copy). */
export const SUMMARY_BRIEF_MAX_CHARS = 280;
export const SUMMARY_MEMBER_DETAIL_MAX_CHARS = 2000;

/** Reasons the grounding verify drops a clinician claim. */
export const GROUNDING_DROP_REASON = ['quote_not_found', 'bad_turn_index', 'wrong_role'] as const;
export const groundingDropReasonSchema = z.enum(GROUNDING_DROP_REASON);
export type GroundingDropReason = z.infer<typeof groundingDropReasonSchema>;

/** `schema_version` written on every new row. */
export const SUMMARY_SCHEMA_VERSION = 1;

/**
 * Stored clinician artifact (`conversation_summaries.clinician` jsonb). Every
 * item's `quote` + `turnIndex` is its surviving citation: the quote was
 * mechanically verified to exist contiguously inside
 * the authoritative source-bounded message projection at `turnIndex`, so a
 * reader can deep-link the claim to its source turn. Closed shapes — we are
 * the only writer.
 */
export const clinicianSectionItemSchema = z
  .object({
    text: z.string().min(1),
    quote: z.string().min(1),
    turnIndex: z.number().int().nonnegative(),
  })
  .strict();
export type ClinicianSectionItem = z.infer<typeof clinicianSectionItemSchema>;

export const clinicianSectionSchema = z
  .object({
    sectionKey: clinicianSectionKeySchema,
    items: z.array(clinicianSectionItemSchema).min(1),
  })
  .strict();
export type ClinicianSection = z.infer<typeof clinicianSectionSchema>;

export const clinicianSummarySchema = z
  .object({
    highLevel: z.string().min(1),
    sections: z.array(clinicianSectionSchema),
  })
  .strict();
export type ClinicianSummary = z.infer<typeof clinicianSummarySchema>;

/**
 * Stored grounding audit (`conversation_summaries.grounding` jsonb) — counts
 * and reason classes only, never claim text (the dropped claims exist in
 * `raw_output` for row-level audit).
 */
export const summaryGroundingSchema = z
  .object({
    claimsTotal: z.number().int().nonnegative(),
    claimsDropped: z.number().int().nonnegative(),
    droppedByReason: z.partialRecord(groundingDropReasonSchema, z.number().int().nonnegative()),
  })
  .strict();
export type SummaryGrounding = z.infer<typeof summaryGroundingSchema>;

/**
 * Stored-row projection consumed by prompt-context readers of
 * `conversation_summaries` (ADR-088 Rule 3: prompt-read modules parse stored
 * payloads through the owning domain's shared schema, never an `as`-cast).
 * Deliberately tolerant of extra keys — it parses a DB row projection, not a
 * wire payload — and the member artifacts are independently nullable: the
 * safety gate nulls them even on non-skip statuses, so a reader must treat
 * each artifact as optional per row. `createdAt` is the CONVERSATION's
 * timestamp (the reader's repo joins it), never the summary row's persist
 * time — recency claims must reflect when the member actually talked.
 */
export const summaryPromptFeedRowSchema = z.object({
  brief: z.string().nullable(),
  memberDetail: z.string().nullable(),
  substantive: z.boolean(),
  status: summaryStatusSchema,
  createdAt: z.coerce.date(),
});
export type SummaryPromptFeedRow = z.infer<typeof summaryPromptFeedRowSchema>;

/**
 * The summarizer model's wire contract. Required fields missing → the adapter
 * throws (retry → DLQ, never an invented artifact). Everything recoverable is
 * deliberately tolerant instead: unknown top-level keys are stripped, unknown
 * `section_key`/topic values are dropped code-side (a temperature-0 retry
 * would reproduce them — rejecting the response over one stray label discards
 * good artifacts), and an item missing its quote/turn_index falls to the
 * grounding verify as an undropped-citation failure rather than a parse error.
 */
export const summaryModelSectionItemSchema = z.object({
  text: z.string().min(1),
  quote: z.string().optional(),
  turn_index: z.number().int().optional(),
});
export type SummaryModelSectionItem = z.infer<typeof summaryModelSectionItemSchema>;

export const summaryModelSectionSchema = z.object({
  section_key: z.string(),
  items: z.array(summaryModelSectionItemSchema).min(1),
});
export type SummaryModelSection = z.infer<typeof summaryModelSectionSchema>;

export const summaryModelOutputSchema = z.object({
  brief: z.string().min(1),
  member_detail: z.string().min(1),
  clinician: z.object({
    high_level: z.string().min(1),
    sections: z.array(summaryModelSectionSchema).default([]),
  }),
  topics: z.array(z.string()).default([]),
  substantive: z.boolean(),
  crisis: z.boolean().default(false),
});
export type SummaryModelOutput = z.infer<typeof summaryModelOutputSchema>;
